/**
 * NotificationDropdown Component
 * Dropdown panel showing recent notifications
 */

import React from 'react';
import { Button, Empty, Spin } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  useUnreadNotifications,
  useBulkMarkAsRead,
} from '@/hooks/useNotifications';
import { NotificationItem } from './NotificationItem';
import './NotificationDropdown.css';

interface NotificationDropdownProps {
  onClose: () => void;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  onClose,
}) => {
  const navigate = useNavigate();
  const { data: notifications, isLoading } = useUnreadNotifications({
    limit: 10,
  });
  const { mutate: markAllAsRead, isPending } = useBulkMarkAsRead();

  const handleMarkAllAsRead = () => {
    if (!notifications || notifications.length === 0) return;

    const ids = notifications.map((n) => n.id);
    markAllAsRead({ notification_ids: ids });
  };

  const handleViewAll = () => {
    navigate('/notifications');
    onClose();
  };

  const handleSettings = () => {
    navigate('/settings/notifications');
    onClose();
  };

  return (
    <div className="notification-dropdown">
      {/* Header */}
      <div className="notification-dropdown-header">
        <h3>Notifications</h3>
        <div className="notification-dropdown-actions">
          <Button
            type="text"
            size="small"
            onClick={handleMarkAllAsRead}
            disabled={!notifications?.length || isPending}
            loading={isPending}
          >
            Mark all as read
          </Button>
          <SettingOutlined
            onClick={handleSettings}
            style={{ cursor: 'pointer', fontSize: '16px' }}
          />
        </div>
      </div>

      {/* List */}
      <div className="notification-dropdown-list">
        {isLoading && (
          <div className="notification-dropdown-loading">
            <Spin />
          </div>
        )}
        {!isLoading && !notifications?.length && (
          <Empty
            description="No new notifications"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
        {notifications?.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onClick={onClose}
          />
        ))}
      </div>

      {/* Footer */}
      {notifications && notifications.length > 0 && (
        <div className="notification-dropdown-footer">
          <Button type="link" onClick={handleViewAll} block>
            View all notifications
          </Button>
        </div>
      )}
    </div>
  );
};
